import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const PrivacyPolicyPage: React.FC = () => {
  const { language } = useLanguage();
  const isTr = language === 'tr';

  type ContentType = {
    title: string;
    lastUpdated: string;
    sections: Array<{
      title: string;
      content: string[];
    }>;
  };

  const content: Record<string, ContentType> = {
    en: {
      title: 'Privacy Policy',
      lastUpdated: 'Last Updated: January 2025',
      sections: [
        {
          title: '1. Information We Collect',
          content: [
            'We collect information that you provide directly to us, including:',
            '• Account information (username, email, name)',
            '• Content you upload (scripts, PDFs)',
            '• Usage data and analytics',
            '• Communication data when you contact us',
          ],
        },
        {
          title: '2. How We Use Your Information',
          content: [
            'We use the information we collect to:',
            '• Provide and improve our services',
            '• Process your script analyses',
            '• Communicate with you about your account',
            '• Send you important updates and notifications',
            '• Ensure security and prevent fraud',
          ],
        },
        {
          title: '3. Data Storage and Security',
          content: [
            'We implement appropriate technical and organizational measures to protect your data:',
            '• All data is encrypted in transit and at rest',
            '• Access to your data is restricted to authorized personnel only',
            '• We regularly review and update our security practices',
            '• Your uploaded scripts are stored securely and can be deleted at any time',
          ],
        },
        {
          title: '4. Cookies and Tracking',
          content: [
            'We use cookies and similar technologies to:',
            '• Maintain your session and authentication',
            '• Remember your preferences (language, theme)',
            '• Analyze usage patterns to improve our service',
            'You can control cookies through your browser settings.',
          ],
        },
        {
          title: '5. Your Rights',
          content: [
            'You have the right to:',
            '• Access your personal data',
            '• Correct inaccurate data',
            '• Request deletion of your data',
            '• Object to processing of your data',
            '• Data portability',
            'To exercise these rights, please contact us through our contact form.',
          ],
        },
        {
          title: '6. Data Sharing',
          content: [
            'We do not sell your personal data. We may share data only:',
            '• With your explicit consent',
            '• To comply with legal obligations',
            '• With service providers who assist in our operations (under strict confidentiality agreements)',
          ],
        },
        {
          title: '7. Changes to This Policy',
          content: [
            'We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last Updated" date.',
          ],
        },
        {
          title: '8. Contact Us',
          content: [
            'If you have any questions about this Privacy Policy, please contact us through our contact form or email us at privacy@mrmirror.ai',
          ],
        },
      ],
    },
    tr: {
      title: 'Gizlilik Politikası',
      lastUpdated: 'Son Güncelleme: Ocak 2025',
      sections: [
        {
          title: '1. Topladığımız Bilgiler',
          content: [
            'Doğrudan bize sağladığınız bilgileri topluyoruz, bunlar:',
            '• Hesap bilgileri (kullanıcı adı, e-posta, isim)',
            '• Yüklediğiniz içerikler (senaryolar, PDF\'ler)',
            '• Kullanım verileri ve analitikler',
            '• Bizimle iletişime geçtiğinizde iletişim verileri',
          ],
        },
        {
          title: '2. Bilgilerinizi Nasıl Kullanıyoruz',
          content: [
            'Topladığımız bilgileri şu amaçlarla kullanıyoruz:',
            '• Hizmetlerimizi sağlamak ve iyileştirmek',
            '• Senaryo analizlerinizi işlemek',
            '• Hesabınız hakkında sizinle iletişim kurmak',
            '• Önemli güncellemeler ve bildirimler göndermek',
            '• Güvenliği sağlamak ve dolandırıcılığı önlemek',
          ],
        },
        {
          title: '3. Veri Depolama ve Güvenlik',
          content: [
            'Verilerinizi korumak için uygun teknik ve organizasyonel önlemler alıyoruz:',
            '• Tüm veriler aktarım sırasında ve depolamada şifrelenir',
            '• Verilerinize erişim yalnızca yetkili personelle sınırlıdır',
            '• Güvenlik uygulamalarımızı düzenli olarak gözden geçirir ve güncelleriz',
            '• Yüklediğiniz senaryolar güvenli bir şekilde saklanır ve istediğiniz zaman silinebilir',
          ],
        },
        {
          title: '4. Çerezler ve İzleme',
          content: [
            'Çerezleri ve benzer teknolojileri şu amaçlarla kullanıyoruz:',
            '• Oturumunuzu ve kimlik doğrulamanızı sürdürmek',
            '• Tercihlerinizi hatırlamak (dil, tema)',
            '• Hizmetimizi iyileştirmek için kullanım kalıplarını analiz etmek',
            'Çerezleri tarayıcı ayarlarınızdan kontrol edebilirsiniz.',
          ],
        },
        {
          title: '5. Haklarınız',
          content: [
            'Şu haklara sahipsiniz:',
            '• Kişisel verilerinize erişim',
            '• Yanlış verileri düzeltme',
            '• Verilerinizin silinmesini talep etme',
            '• Verilerinizin işlenmesine itiraz etme',
            '• Veri taşınabilirliği',
            'Bu haklarınızı kullanmak için lütfen iletişim formumuz aracılığıyla bizimle iletişime geçin.',
          ],
        },
        {
          title: '6. Veri Paylaşımı',
          content: [
            'Kişisel verilerinizi satmıyoruz. Verileri yalnızca şu durumlarda paylaşabiliriz:',
            '• Açık rızanızla',
            '• Yasal yükümlülüklere uymak için',
            '• Operasyonlarımıza yardımcı olan hizmet sağlayıcılarla (katı gizlilik anlaşmaları altında)',
          ],
        },
        {
          title: '7. Bu Politikanın Değişiklikleri',
          content: [
            'Bu Gizlilik Politikasını zaman zaman güncelleyebiliriz. Değişiklikleri bu sayfada yeni politikayı yayınlayarak ve "Son Güncelleme" tarihini güncelleyerek size bildireceğiz.',
          ],
        },
        {
          title: '8. Bizimle İletişime Geçin',
          content: [
            'Bu Gizlilik Politikası hakkında herhangi bir sorunuz varsa, lütfen iletişim formumuz aracılığıyla bizimle iletişime geçin veya privacy@mrmirror.ai adresine e-posta gönderin',
          ],
        },
      ],
    },
  };

  const policy: ContentType = content[language] || content.en;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />

      <main className="flex-1 max-w-4xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24 w-full">
        <div className="flex items-baseline justify-between mb-12">
          <span className="eyebrow"><span className="text-accent">/01</span> · {isTr ? 'YASAL' : 'LEGAL'}</span>
          <span className="eyebrow-mute hidden md:inline">{policy.lastUpdated.toUpperCase()}</span>
        </div>

        <div className="mb-16 pb-12 border-b border-hairline">
          <h1 className="display-h display-lg mb-4">
            {policy.title}
          </h1>
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-mute">
            {policy.lastUpdated}
          </p>
        </div>

        <article className="space-y-14">
          {policy.sections.map((section, index) => (
            <section key={index} className="grid grid-cols-12 gap-6 md:gap-10">
              <div className="col-span-12 md:col-span-3">
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-accent block mb-2">
                  /{String(index + 1).padStart(2, '0')}
                </span>
                <h2 className="font-serif italic text-xl md:text-2xl text-ink leading-tight">
                  {section.title.replace(/^\d+\.\s*/, '')}
                </h2>
              </div>
              <div className="col-span-12 md:col-span-9">
                <ul className="space-y-3 font-prose text-[15px] leading-relaxed text-ink-dim">
                  {section.content.map((item, itemIndex) => (
                    <li key={itemIndex} className="leading-relaxed">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          ))}
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;

